import React, { useEffect, useContext } from "react";
import axios from "axios";

import "./Styles/Menu.css";
import logo from "../Images/Logos/SAFRIPOL Logo Horizontal.png";

import { Person } from "./Person";

import {
  Nav,
  CommandBar,
  Panel,
  PanelType,
  Text,
  Stack,
} from "@fluentui/react";
import { getTheme } from "@fluentui/react";
// import { useConstCallback } from "@uifabric/react-hooks";

import { MenuContext } from "./Contexts/MenuContext";
import { FrameContext } from "./Contexts/FrameContext";
import { fetchAvatar } from "./Functions/Fetch";
import { useMsal } from "@azure/msal-react";
import { handleMenuClick } from "./Functions/Handlers";
import BlobStorageService from "./Functions/BlobStorageService";
import * as utilMenu from "./Functions/util-menu";

const MENU_URL = process.env.PUBLIC_URL + "/menu.json";
const MENUBAR_URL = process.env.PUBLIC_URL + "/menu-bar.json";
const HOME_URL = process.env.PUBLIC_URL + "/home.json";

export const Menu = () => {
  const { instance, accounts } = useMsal();
  const [menuContext, setMenuContext] = useContext(MenuContext);
  const [, setFrameContext] = useContext(FrameContext);

  useEffect(() => {
    fetchMenuAsync(setMenuContext, setFrameContext);
    fetchMenuBarAsync(setMenuContext, setFrameContext);
    fetchHomeAsync(setMenuContext);
    fetchAvatarAsync(setMenuContext, instance, accounts);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // will override fetchHome if a name and url are included as parameters
    let href = new URL(window.location.href);
    let searchParams = href.searchParams;
    let name = searchParams.get("name");
    let url = searchParams.get("url");

    if (menuContext.home) {
      handleMenuClick(
        setFrameContext,
        null,
        name === null ? menuContext.home.text : name,
        url === null ? menuContext.home.href : url,
        "frame"
      );
    }
  }, [menuContext.home, setFrameContext]);

  return <MenuPanel />;
};

const fetchAvatarAsync = async (setMenuContext, instance, accounts) => {
  try {
    const response = await fetchAvatar(instance, accounts);
    setMenuContext((prevState) => ({
      ...prevState,
      avatar: response,
      loadingAvatar: false,
    }));
  } catch (e) {
    setMenuContext((prevState) => ({
      ...prevState,
      loadingAvatar: false,
    }));
  }
};

export const fetchMenuAsync = async (setMenuContext, setFrameContext) => {
  try {
    const response = await axios.get(
      `${MENU_URL}?timestamp=${new Date().toISOString()}`
    );
    let menu = JSON.parse(JSON.stringify(response.data.menu));
    menu = menu.map(
      (x) => (x = addCallbackToFabricNavGroup(setFrameContext, x))
    );
    let menuEdit = utilMenu.toIterativeMenu(response.data.menu);
    setMenuContext((prevState) => ({
      ...prevState,
      menu: menu,
      menuEdit: menuEdit,
      loadingMenu: false,
    }));
  } catch (e) {
    console.log(e);
    setMenuContext((prevState) => ({
      ...prevState,
      loadingMenu: false,
    }));
  }
};

const fetchMenuBarAsync = async (setMenuContext, setFrameContext) => {
  try {
    const response = await axios.get(
      `${MENUBAR_URL}?timestamp=${new Date().toISOString()}`
    );
    let data = response.data.menuBar?.map(
      (x) => (x = addCallbackToFabricCommandBarItem(setFrameContext, x))
    );
    data.push({
      key: 9999,
      text: "Edit",
      ariaLabel: "Edit",
      iconOnly: true,
      disabled: false,
      iconProps: {
        iconName: "Edit",
      },
      onClick: () => {
        setMenuContext((prevState) => ({
          ...prevState,
          isEditing: !prevState.isEditing,
        }));
      },
    });

    setMenuContext((prevState) => ({
      ...prevState,
      menuBar: data,
      loadingMenuBar: false,
    }));
  } catch (e) {
    console.log(e);
    setMenuContext((prevState) => ({
      ...prevState,
      loadingMenuBar: false,
    }));
  }
};

const fetchHomeAsync = async (setMenuContext) => {
  try {
    const response = await axios.get(HOME_URL);
    setMenuContext((prevState) => ({
      ...prevState,
      home: response.data,
    }));
  } catch (e) {
    console.log(e);
  }
};

const addCallbackToFabricNavGroup = (setFrameContext, data) => {
  if (!data) {
    console.log("No data");
    return;
  }
  // recursively set the nested items
  let newData = JSON.parse(JSON.stringify(data));
  newData = {
    ...newData,
    onClick: (event) =>
      handleMenuClick(setFrameContext, event, data.name, data.url, data.target),
    links: newData.links?.map(
      (x) => addCallbackToFabricNavGroup(setFrameContext, x) ?? []
    ),
  };
  return newData;
};

const addCallbackToFabricCommandBarItem = (setFrameContext, data) => {
  if (!data) {
    return;
  }
  data = {
    ...data,
    onClick: (event) => {
      handleMenuClick(
        setFrameContext,
        event,
        data.text,
        data.href,
        data.target
      );
      BlobStorageService.init();
    },
  };
  return data;
};

export const MenuPanel = (props) => {
  const [menuContext, setMenuContext] = useContext(MenuContext);

  const textStyles = {
    root: {
      color: getTheme().palette.neutralPrimary,
    },
  };

  const panelType = PanelType.customNear;
  const menuWidth = "320px";

  return (
    <div>
      <Panel
        type={panelType}
        isOpen={menuContext.isMenuOpen}
        onDismiss={() =>
          setMenuContext((prevState) => ({
            ...prevState,
            isMenuOpen: false,
          }))
        }
        isLightDismiss={true}
        isHiddenOnDismiss={true}
        customWidth={
          panelType === PanelType.custom || panelType === PanelType.customNear
            ? menuWidth
            : undefined
        }
        closeButtonAriaLabel="Close"
        className="Panel-menu"
        styles={{
          main: { backgroundColor: getTheme().palette.neutralPrimary },
        }}
      >
        <Stack
          className="Menu"
          styles={{
            root: { background: getTheme().palette.neutralPrimary, minWidth: "100%" },
          }}
        >
          <Stack.Item className="Menu-header"
            styles={{
              root: { background: getTheme().palette.neutralPrimary },
            }}
          >
            <a
              href={`?name=${menuContext.home?.text}&url=${menuContext.home?.href}`}
            >
              <img src={logo} className="Menu-logo" alt="logo" />
            </a>
          </Stack.Item>
          <Stack.Item className="Menu-header">
            {menuContext.loadingMenuBar ? (
              <Text styles={textStyles}> Fetching menu bar... </Text>
            ) : (
              <CommandBar className="Menu-Bar" items={menuContext.menuBar}
                 styles={{
                   root: { backgroundColor: getTheme().palette.neutralPrimary },
                 }}
              />
            )}
          </Stack.Item>
          <Stack.Item className="Menu-content">
            {menuContext.loadingMenu ? (
              <Text styles={textStyles}>Fetching menu...</Text>
            ) : (
              <Nav className="Menu-Nav" groups={menuContext.menu}
                styles={{
                  link: {
                    '.ms-Nav-compositeLink:hover &': {                    
                      backgroundColor: '#fff'
                    }
                  },
                  linkText: {
                    color: '#0f206c',
                    '.ms-Nav-compositeLink:hover &': {
                      color: '#236092',
                    }
                  }
                }} />
            )}
          </Stack.Item>
          <Stack.Item
            styles={{
              root: {
                textAlign: "left",
              },
            }}
          >
            <Text
              styles={{
                root: {
                  color: getTheme().palette.neutralPrimary,
                  marginLeft: "12px",
                },
              }}
              variant="xSmall"
            >
              version {process.env.REACT_APP_VERSION}
            </Text>
          </Stack.Item>
          {menuContext.useAnonymous !== null &&
            (menuContext.useAnonymous || (
              <Stack.Item
                className="Menu-footer"
                styles={{
                  root: { background: getTheme().palette.neutralPrimary },
                }}
              >
                <div>
                  <Person />
                </div>
              </Stack.Item>
            ))}
        </Stack>
      </Panel>
    </div>
  );
};
